import { Suspense, useLayoutEffect, useRef, type ReactNode } from "react";
import { MainPaneLoader } from "./MainPaneLoader";
import { SidebarInset } from "./ui/sidebar";
import { cn } from "@repo/blacksmith-ui/cn";

/**
 * The main pane and its Suspense boundary stay mounted across route changes.
 * React only holds the current page through a transition when the boundary
 * that would catch the next page's suspension is already on screen; a
 * remounted boundary shows its fallback the moment the route commits, so
 * every route change flashed a blank pane before the next page appeared.
 */
export function SidebarMainContent({
  children,
  fixedMain,
  scrollResetKey,
}: {
  children: ReactNode;
  fixedMain: boolean;
  /** Scroll returns to the top when this changes (route pattern, not params). */
  scrollResetKey: string;
}) {
  const mainRef = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    const main = mainRef.current;
    if (!main) return;
    main.scrollTop = 0;
    main.scrollLeft = 0;
  }, [scrollResetKey]);

  return (
    <SidebarInset
      ref={mainRef}
      className={cn(
        "flex-1 bg-transparent!",
        fixedMain
          ? "min-h-0 overflow-hidden"
          : "overflow-y-auto [scrollbar-gutter:stable]",
      )}
    >
      <Suspense fallback={<MainPaneLoader />}>{children}</Suspense>
    </SidebarInset>
  );
}
